import { Flight, Passenger } from '../types';

interface BookingSummaryProps {
  flight: Flight | null;
  passengers: Passenger[];
  departureDate: string;
  returnDate: string | null;
  totalPrice: number;
}

export function BookingSummary({
  flight,
  passengers,
  departureDate,
  returnDate,
  totalPrice,
}: BookingSummaryProps) {
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="w-full bg-orbital-card border border-orbital-dim p-5">
      <div className="flex items-center gap-2 mb-4 border-b border-orbital-dim pb-3">
        <span className="text-xl">📋</span>
        <h2 className="font-mono text-orbital-cyan uppercase tracking-widest">
          Booking Summary
        </h2>
      </div>

      {/* Flight info */}
      {flight && (
        <div className="mb-4">
          <div className="text-xs text-orbital-magenta font-mono mb-1">[FLIGHT]</div>
          <div className="flex justify-between items-start">
            <div>
              <div className="text-orbital-primary font-mono text-sm">{flight.destination}</div>
              <div className="text-xs text-gray-400 mt-1">
                {flight.icon} {flight.class.toUpperCase()} CLASS
              </div>
            </div>
            <div className="text-right text-xs text-gray-500 font-mono">
              <div>{formatDate(departureDate)}</div>
              {returnDate && <div>Return: {formatDate(returnDate)}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Passenger breakdown */}
      <div className="mb-4">
        <div className="text-xs text-orbital-magenta font-mono mb-2">[PASSENGERS]</div>
        <div className="space-y-1">
          {passengers.map((p, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span className="text-gray-300 font-mono">
                {p.firstName || 'UNNAMED'} {p.lastName || 'CREW'}
              </span>
              <span className="text-orbital-yellow">
                ${(flight?.price || 0).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Price breakdown */}
      <div className="border-t border-orbital-dim pt-3 space-y-2">
        <div className="flex justify-between text-sm text-gray-400">
          <span>Subtotal ({passengers.length}x)</span>
          <span>${totalPrice.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-sm text-gray-400">
          <span>Insurance Fee (2.5%)</span>
          <span>${Math.round(totalPrice * 0.025).toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-lg font-mono text-orbital-yellow border-t border-orbital-dim pt-2">
          <span>TOTAL</span>
          <span>${Math.round(totalPrice * 1.025).toLocaleString()}</span>
        </div>
      </div>

      {/* Fee disclaimer */}
      <div className="mt-3 text-xs text-gray-600 font-mono">
        * All prices in USD. Orbital insurance included. Cancellation fees apply.
      </div>
    </div>
  );
}
